import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, Check, Eye, Pencil, ChevronDown, ChevronUp, Monitor } from 'lucide-react';
import { farmvestService } from '../services/farmvest_api';

// ─── Module / Screen / Action config ────────────────────────────────────────

interface Action {
    key: string;   // dot-notation, e.g. "PAYMENTS.VERIFY"
    label: string;
}
interface Screen {
    key: string;
    label: string;
    actions: Action[];
}
interface Module {
    key: string;
    label: string;
    color: string;      // header accent color
    screens: Screen[];
}

const MODULES: Module[] = [
    {
        key: 'FARM_OPS', label: 'Farm Operations', color: '#15803D',
        screens: [
            { key: 'DASHBOARD', label: 'Dashboard', actions: [] },
            { key: 'FARMS', label: 'Farms (create/Edit farm)', actions: [] },
            { key: 'ANIMAL_ONBOARDING', label: 'Animal Onboarding (Add/Onboard Animal)', actions: [] },
            { key: 'BUFFALO', label: 'Buffalo', actions: [] },
            { key: 'UNALLOCATED_ANIMALS', label: 'Unallocated Animals', actions: [] },
            { key: 'MILK_PRODUCTION', label: 'Milk Production', actions: [] },
            { key: 'ROSTER', label: 'Roster', actions: [] },
        ],
    },
    {
        key: 'STAFF', label: 'Staff & HR', color: '#7C3AED',
        screens: [
            { key: 'EMPLOYEES', label: 'Employee (Add/Edit Employee)', actions: [] },
            { key: 'LEAVE_REQUESTS', label: 'Leave Requests', actions: [] },
        ],
    },
    {
        key: 'FINANCE', label: 'Finance', color: '#1D4ED8',
        screens: [
            {
                key: 'PAYMENTS', label: 'Payments', actions: [
                    { key: 'PAYMENTS.ADD_CPF_DETAILS', label: 'Add CPF Details' },
                    { key: 'PAYMENTS.VERIFY_REJECT', label: 'Verify and Reject Buttons' },
                ]
            },
            { key: 'REVENUE', label: 'Due Payouts', actions: [] },
            { key: 'WALLET', label: 'Wallet', actions: [] },
        ],
    },
    {
        key: 'HEALTH', label: 'Health & Veterinary', color: '#B45309',
        screens: [
            { key: 'TICKETS', label: 'Health Tickets', actions: [] },
            { key: 'UNIT_TRANSFER', label: 'Unit Transfer', actions: [] },
            { key: 'IOT_ALERTS', label: 'IoT Alerts', actions: [] },
        ],
    },
    {
        key: 'BUSINESS', label: 'Business & Analytics', color: '#0E7490',
        screens: [
            {
                key: 'INVESTORS', label: 'Unit Owners', actions: [
                    { key: 'DIGITAL_MOU', label: 'Digital MOU' },
                    { key: 'DIGITAL_MOU.EDIT_FIELDS', label: 'Edit MOU Fields' },
                ]
            },
            {
                key: 'INVESTOR_ONBOARDING', label: 'Owner Onboarding', actions: [
                    { key: 'INVESTOR_ONBOARDING.VERIFY_REJECT', label: 'Verify and Reject Buttons' }
                ]
            },
            { key: 'FARM_VISIT', label: 'Farm Visit', actions: [] },
            { key: 'INVENTORY', label: 'Inventory', actions: [] },
        ],
    },
];

// All keys in a flat list for "All Screens" logic
export const ALL_KEYS: string[] = MODULES.flatMap(m =>
    m.screens.flatMap(s => [s.key, ...s.actions.map(a => a.key)])
);

// Total top-level screens (no dot-notation actions) — used for count display in table
export const TOTAL_SCREENS: number = MODULES.flatMap(m => m.screens).length;

// ─── Access helpers ──────────────────────────────────────────────────────────

type Level = 'full' | 'view' | 'none';

function getLevel(access: Record<string, string>, key: string): Level {
    const v = access[key];
    if (v === 'none') return 'none';
    if (v === 'view') return 'view';
    return v === 'full' ? 'full' : 'none';
}

function canView(access: Record<string, string>, key: string): boolean {
    return getLevel(access, key) !== 'none';
}

function canEdit(access: Record<string, string>, key: string): boolean {
    return getLevel(access, key) === 'full';
}

function setView(access: Record<string, string>, key: string, on: boolean): Record<string, string> {
    const next = { ...access };
    if (!on) {
        next[key] = 'none';
    } else {
        // restore to 'view' if was 'none', keep 'full' if already full
        next[key] = canEdit(next, key) ? 'full' : 'view';
    }
    return next;
}

function setEdit(access: Record<string, string>, key: string, on: boolean): Record<string, string> {
    const next = { ...access };
    if (on) {
        next[key] = 'full';
    } else {
        // if view was on, downgrade to view; else keep none
        next[key] = canView(next, key) ? 'view' : 'none';
    }
    return next;
}

// ─── Modal props ─────────────────────────────────────────────────────────────

interface ScreenAccessModalProps {
    isOpen: boolean;
    onClose: () => void;
    employee: any;
    onSuccess?: (updatedAccess: Record<string, string>) => void;
}

// ─── Component ───────────────────────────────────────────────────────────────

const ScreenAccessModal: React.FC<ScreenAccessModalProps> = ({ isOpen, onClose, employee, onSuccess }) => {
    const [access, setAccess] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [saved, setSaved] = useState(false);
    const [search, setSearch] = useState('');
    const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());
    const [expandedScreens, setExpandedScreens] = useState<Set<string>>(new Set());

    useEffect(() => {
        if (!isOpen || !employee) return;
        let isMounted = true;
        setSearch('');
        setExpandedModules(new Set());
        setExpandedScreens(new Set());
        const fetchAccess = async () => {
            setLoading(true); setError(''); setSaved(false);
            try {
                // 1. Fetch employee overrides
                const overrideRes = await farmvestService.getScreenAccess(employee.user_id || employee.id);
                const backendOverrides = overrideRes.data?.screen_access || {};
                const overrides: Record<string, string> = {};
                Object.entries(backendOverrides).forEach(([k, v]) => {
                    overrides[k.toUpperCase()] = String(v);
                });

                // 2. Fetch all role templates to compute the baseline inherited permissions
                let mergedRoleAccess: Record<string, string> = {};
                try {
                    const rolesRes = await farmvestService.getAllRoles();
                    const roles = rolesRes.roles || [];
                    let empRoles: string[] = [];
                    if (employee) {
                        if (Array.isArray(employee.roles_detail)) {
                            empRoles = employee.roles_detail.map((rd: any) => rd.role).filter(Boolean);
                        } else if (Array.isArray(employee.roles)) {
                            empRoles = employee.roles;
                        } else if (typeof employee.roles === 'string') {
                            empRoles = employee.roles.split(',').map((r: string) => r.trim());
                        } else if (employee.role) {
                            empRoles = [employee.role];
                        }
                    }

                    empRoles.forEach((roleName: string) => {
                        const roleTemplate = roles.find(r => r.name.toLowerCase() === roleName.toLowerCase());
                        if (roleTemplate && roleTemplate.screen_access) {
                            Object.entries(roleTemplate.screen_access).forEach(([key, val]) => {
                                const current = mergedRoleAccess[key.toUpperCase()];
                                const newVal = String(val).toLowerCase();
                                if (!current || newVal === 'full' || (newVal === 'view' && current === 'none')) {
                                    mergedRoleAccess[key.toUpperCase()] = newVal;
                                }
                            });
                        }
                    });
                } catch (roleErr) {
                    console.error('Failed to load role templates in ScreenAccessModal:', roleErr);
                }

                // 3. Construct resolved access: merge user overrides and role template defaults with user overrides taking precedence
                const resolvedAccess: Record<string, string> = {};
                ALL_KEYS.forEach(key => {
                    const rAccess = mergedRoleAccess[key] || 'none';
                    if (overrides[key] !== undefined) {
                        resolvedAccess[key] = overrides[key];
                    } else {
                        resolvedAccess[key] = rAccess;
                    }
                });

                if (isMounted) {
                    setAccess(resolvedAccess);
                }
            } catch (err: any) {
                if (isMounted) {
                    setError('Failed to load screen access configuration.');
                    // Fallback using whatever is on employee object
                    const resolvedAccess: Record<string, string> = {};
                    const fallbackOverrides = employee.screen_access || {};
                    const fallbackUpper: Record<string, string> = {};
                    Object.entries(fallbackOverrides).forEach(([k, v]) => {
                        fallbackUpper[k.toUpperCase()] = String(v);
                    });

                    // Fetch all role templates for fallback baseline as well
                    let mergedRoleAccess: Record<string, string> = {};
                    try {
                        const rolesRes = await farmvestService.getAllRoles();
                        const roles = rolesRes.roles || [];
                        let empRoles: string[] = [];
                        if (employee) {
                            if (Array.isArray(employee.roles_detail)) {
                                empRoles = employee.roles_detail.map((rd: any) => rd.role).filter(Boolean);
                            } else if (Array.isArray(employee.roles)) {
                                empRoles = employee.roles;
                            } else if (typeof employee.roles === 'string') {
                                empRoles = employee.roles.split(',').map((r: string) => r.trim());
                            } else if (employee.role) {
                                empRoles = [employee.role];
                            }
                        }

                        empRoles.forEach((roleName: string) => {
                            const roleTemplate = roles.find(r => r.name.toLowerCase() === roleName.toLowerCase());
                            if (roleTemplate && roleTemplate.screen_access) {
                                Object.entries(roleTemplate.screen_access).forEach(([key, val]) => {
                                    const current = mergedRoleAccess[key.toUpperCase()];
                                    const newVal = String(val).toLowerCase();
                                    if (!current || newVal === 'full' || (newVal === 'view' && current === 'none')) {
                                        mergedRoleAccess[key.toUpperCase()] = newVal;
                                    }
                                });
                            }
                        });
                    } catch (roleErr) {
                        console.error('Failed to load role templates in ScreenAccessModal fallback:', roleErr);
                    }

                    ALL_KEYS.forEach(key => {
                        const rAccess = mergedRoleAccess[key] || 'none';
                        if (fallbackUpper[key] !== undefined) {
                            resolvedAccess[key] = fallbackUpper[key];
                        } else {
                            resolvedAccess[key] = rAccess;
                        }
                    });
                    setAccess(resolvedAccess);
                }
            } finally {
                if (isMounted) setLoading(false);
            }
        };
        fetchAccess();
        return () => { isMounted = false; };
    }, [isOpen, employee]);

    // ── Filtered modules based on search ──
    const filteredModules = useMemo(() => {
        if (!search.trim()) return MODULES;
        const q = search.toLowerCase();
        return MODULES.map(m => ({
            ...m,
            screens: m.screens
                .map(s => ({
                    ...s,
                    actions: s.actions.filter(a => a.label.toLowerCase().includes(q)),
                }))
                .filter(s => s.label.toLowerCase().includes(q) || s.actions.length > 0),
        })).filter(m => m.label.toLowerCase().includes(q) || m.screens.length > 0);
    }, [search]);

    // ── All-screens state ──
    const allFull = ALL_KEYS.every(k => getLevel(access, k) === 'full');
    const allView = ALL_KEYS.every(k => canView(access, k));
    const allEdit = ALL_KEYS.every(k => canEdit(access, k));
    const someEnabled = ALL_KEYS.some(k => getLevel(access, k) !== 'none');
    // Keep pencil/edit buttons always visible so they can be selected individually
    const showEditButtons = true;

    const toggleAll = () => {
        const next: Record<string, string> = { ...access };
        if (allFull) {
            ALL_KEYS.forEach(k => { next[k] = 'none'; });
        } else {
            ALL_KEYS.forEach(k => { next[k] = 'full'; });
        }
        setAccess(next); setSaved(false);
    };

    const toggleAllView = () => {
        let next = { ...access };
        const on = !allView;
        ALL_KEYS.forEach(k => { next = setView(next, k, on); });
        setAccess(next); setSaved(false);
    };

    const toggleAllEdit = () => {
        let next = { ...access };
        const on = !allEdit;
        ALL_KEYS.forEach(k => { next = setEdit(next, k, on); });
        setAccess(next); setSaved(false);
    };

    // ── Module-level toggles ──
    const moduleKeys = (m: Module) => m.screens.flatMap(s => [s.key, ...s.actions.map(a => a.key)]);
    const moduleAllView = (m: Module) => moduleKeys(m).every(k => canView(access, k));
    const moduleAllEdit = (m: Module) => moduleKeys(m).every(k => canEdit(access, k));

    const toggleModuleView = (m: Module) => {
        let next = { ...access };
        const on = !moduleAllView(m);
        moduleKeys(m).forEach(k => { next = setView(next, k, on); });
        setAccess(next); setSaved(false);
    };

    const toggleModuleEdit = (m: Module) => {
        let next = { ...access };
        const on = !moduleAllEdit(m);
        moduleKeys(m).forEach(k => { next = setEdit(next, k, on); });
        setAccess(next); setSaved(false);
    };

    // ── Screen-level toggles ──
    const screenKeys = (s: Screen) => [s.key, ...s.actions.map(a => a.key)];
    const screenAllView = (s: Screen) => screenKeys(s).every(k => canView(access, k));
    const screenAllEdit = (s: Screen) => screenKeys(s).every(k => canEdit(access, k));

    const toggleScreenView = (s: Screen) => {
        let next = { ...access };
        const on = !canView(access, s.key);
        screenKeys(s).forEach(k => { next = setView(next, k, on); });
        setAccess(next); setSaved(false);
    };

    const toggleScreenEdit = (s: Screen) => {
        let next = { ...access };
        const on = !canEdit(access, s.key);
        screenKeys(s).forEach(k => { next = setEdit(next, k, on); });
        setAccess(next); setSaved(false);
    };

    // ── Save ──
    const handleSave = async () => {
        const hasAccess = Object.values(access).some(v => v !== 'none');
        if (!hasAccess) {
            setError('A user must have at least one screen access permission.');
            return;
        }

        setSaving(true); setError('');
        try {
            const apiAccess: Record<string, string> = {};
            Object.entries(access).forEach(([k, v]) => {
                apiAccess[k.toLowerCase()] = v;
            });
            await farmvestService.updateScreenAccess(employee.user_id || employee.id, apiAccess);
            setSaved(true);
            onSuccess?.(access);
            setTimeout(onClose, 800);
        } catch (err: any) {
            const msg = err?.response?.data?.detail || err?.message || 'Failed to save. Please try again.';
            setError(msg);
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen) return null;

    // ── SUPERADMIN guard ──
    const employeeRoles: string[] = Array.isArray(employee?.roles_detail)
        ? employee.roles_detail.map((rd: any) => String(rd.role || '').toUpperCase())
        : [];
    const isSuperAdminTarget = employeeRoles.includes('SUPERADMIN');

    // ── Sub-component: eye/pencil icon toggle ──
    const IconToggle = ({
        active, onClick, icon, activeColor,
    }: { active: boolean; onClick: () => void; icon: React.ReactNode; activeColor: string }) => (
        <button
            onClick={e => { e.stopPropagation(); onClick(); }}
            className="w-7 h-7 rounded-lg flex items-center justify-center transition-all"
            style={{
                background: active ? activeColor + '18' : 'transparent',
                color: active ? activeColor : '#CBD5E1',
                border: active ? `1.5px solid ${activeColor}40` : '1.5px solid transparent',
            }}
        >
            {icon}
        </button>
    );

    return createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={onClose}>
            <div
                className="bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden"
                style={{ width: '100%', maxWidth: 560, maxHeight: '90vh' }}
                onClick={e => e.stopPropagation()}
            >
                {/* ── Header ── */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center">
                            <Monitor size={18} className="text-blue-600" />
                        </div>
                        <div>
                            <h2 className="text-sm font-bold text-gray-900">Screen Access</h2>
                            <p className="text-[11px] text-gray-500">
                                {employee?.first_name} {employee?.last_name}
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg">
                        <X size={16} className="text-gray-500" />
                    </button>
                </div>

                {/* ── SuperAdmin notice ── */}
                {isSuperAdminTarget && (
                    <div className="mx-5 mt-3 mb-1 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mt-0.5 shrink-0"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                        <p className="text-[11px] text-amber-700 leading-tight">
                            <span className="font-semibold">SUPERADMIN always has full access.</span> Screen access configuration does not apply to this role.
                        </p>
                    </div>
                )}

                {/* ── Search ── */}
                <div className="px-5 pt-3 pb-2">
                    <div className="flex items-center gap-2 bg-gray-50 rounded-xl px-3 py-2 border border-gray-200">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2.5"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
                        <input
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Search Screens"
                            className="flex-1 bg-transparent text-[12px] text-gray-700 outline-none placeholder-gray-400"
                        />
                        {search && (
                            <button onClick={() => setSearch('')}>
                                <X size={12} className="text-gray-400" />
                            </button>
                        )}
                    </div>
                </div>

                {/* ── Body ── */}
                <div className="flex-1 overflow-y-auto px-4 pb-4">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 size={24} className="animate-spin text-blue-500" />
                        </div>
                    ) : (
                        <>
                            {/* All Screens */}
                            <div className="flex items-center gap-3 px-2 py-2.5 mb-1">
                                <input
                                    type="checkbox"
                                    checked={allFull}
                                    ref={el => { if (el) el.indeterminate = !allFull && someEnabled; }}
                                    onChange={toggleAll}
                                    className="w-4 h-4 rounded accent-blue-600 cursor-pointer"
                                />
                                <span className="text-[12px] font-bold text-gray-700 flex-1">All Screens</span>
                                <div className="flex items-center gap-1 mr-[30px]">
                                    <IconToggle
                                        active={allView}
                                        onClick={toggleAllView}
                                        icon={<Eye size={13} />}
                                        activeColor="#2563EB"
                                    />
                                    <IconToggle
                                        active={allEdit}
                                        onClick={toggleAllEdit}
                                        icon={<Pencil size={12} />}
                                        activeColor="#2563EB"
                                    />
                                </div>
                            </div>

                            <div className="border-b border-gray-100 mb-2" />

                            {/* Modules */}
                            {filteredModules.map(module => {
                                const modExpanded = expandedModules.has(module.key);
                                const mView = moduleAllView(module);
                                const mEdit = moduleAllEdit(module);

                                return (
                                    <div key={module.key} className="mb-2">
                                        {/* Module header */}
                                        <div
                                            className="flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer select-none"
                                            style={{ background: module.color + '12' }}
                                            onClick={() => {
                                                const next = new Set(expandedModules);
                                                next.has(module.key) ? next.delete(module.key) : next.add(module.key);
                                                setExpandedModules(next);
                                            }}
                                        >
                                            <div
                                                className="w-3 h-3 rounded-sm flex-shrink-0"
                                                style={{ background: module.color }}
                                            />
                                            <span className="text-[12px] font-bold flex-1" style={{ color: module.color }}>
                                                {module.label}
                                            </span>
                                            <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                                                <IconToggle
                                                    active={mView}
                                                    onClick={() => toggleModuleView(module)}
                                                    icon={<Eye size={13} />}
                                                    activeColor={module.color}
                                                />
                                                {showEditButtons && (
                                                    <IconToggle
                                                        active={mEdit}
                                                        onClick={() => toggleModuleEdit(module)}
                                                        icon={<Pencil size={12} />}
                                                        activeColor={module.color}
                                                    />
                                                )}
                                            </div>
                                            <div className="w-6 flex justify-end" style={{ color: module.color }}>
                                                {modExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                            </div>
                                        </div>

                                        {/* Screens */}
                                        {modExpanded && (
                                            <div className="mt-1 flex flex-col gap-0.5 ml-[17px] pl-2 border-l-[2px] border-gray-100">
                                                {module.screens.map(screen => {
                                                    const scrExpanded = expandedScreens.has(screen.key);
                                                    const sView = canView(access, screen.key);
                                                    const sEdit = canEdit(access, screen.key);
                                                    const hasActions = screen.actions.length > 0;

                                                    return (
                                                        <div key={screen.key}>
                                                            {/* Screen row */}
                                                            <div
                                                                className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors"
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    checked={sView}
                                                                    onChange={() => toggleScreenView(screen)}
                                                                    className="w-3.5 h-3.5 rounded accent-blue-600 cursor-pointer flex-shrink-0"
                                                                    onClick={e => e.stopPropagation()}
                                                                />
                                                                <span
                                                                    className="text-[12px] font-semibold flex-1 cursor-pointer"
                                                                    style={{ color: sView ? '#374151' : '#9CA3AF' }}
                                                                    onClick={() => {
                                                                        if (!hasActions) return;
                                                                        const next = new Set(expandedScreens);
                                                                        next.has(screen.key) ? next.delete(screen.key) : next.add(screen.key);
                                                                        setExpandedScreens(next);
                                                                    }}
                                                                >
                                                                    {screen.label}
                                                                </span>
                                                                <div className="flex items-center gap-1">
                                                                    <IconToggle
                                                                        active={sView}
                                                                        onClick={() => toggleScreenView(screen)}
                                                                        icon={<Eye size={13} />}
                                                                        activeColor="#2563EB"
                                                                    />
                                                                    {showEditButtons && (
                                                                        <IconToggle
                                                                            active={sEdit}
                                                                            onClick={() => toggleScreenEdit(screen)}
                                                                            icon={<Pencil size={12} />}
                                                                            activeColor="#2563EB"
                                                                        />
                                                                    )}
                                                                </div>
                                                                <div className="w-6 flex justify-end">
                                                                    {hasActions && (
                                                                        <button
                                                                            className="text-gray-400 hover:text-gray-600"
                                                                            onClick={() => {
                                                                                const next = new Set(expandedScreens);
                                                                                next.has(screen.key) ? next.delete(screen.key) : next.add(screen.key);
                                                                                setExpandedScreens(next);
                                                                            }}
                                                                        >
                                                                            {scrExpanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            {/* Sub-actions */}
                                                            {scrExpanded && (
                                                                <div className="ml-[18px] border-l-[2px] border-gray-100 pl-3 flex flex-col gap-0.5 mb-1">
                                                                    {screen.actions.map(action => {
                                                                        const aView = canView(access, action.key);
                                                                        const aEdit = canEdit(access, action.key);
                                                                        return (
                                                                            <div
                                                                                key={action.key}
                                                                                className="flex items-center gap-3 px-3 py-1.5 rounded-lg bg-gray-50/70"
                                                                            >
                                                                                <input
                                                                                    type="checkbox"
                                                                                    checked={aView}
                                                                                    onChange={e => {
                                                                                        setAccess(prev => {
                                                                                            let next = setView(prev, action.key, e.target.checked);
                                                                                            if (e.target.checked) {
                                                                                                next[screen.key] = getLevel(next, screen.key) === 'full' ? 'full' : 'view';
                                                                                            }
                                                                                            return next;
                                                                                        });
                                                                                        setSaved(false);
                                                                                    }}
                                                                                    className="w-3 h-3 rounded accent-blue-600 cursor-pointer flex-shrink-0"
                                                                                />
                                                                                <span
                                                                                    className="text-[11px] font-medium flex-1"
                                                                                    style={{ color: aView ? '#4B5563' : '#9CA3AF' }}
                                                                                >
                                                                                    {action.label}
                                                                                </span>
                                                                                <div className="flex items-center gap-1">
                                                                                    <IconToggle
                                                                                        active={aView}
                                                                                        onClick={() => {
                                                                                            setAccess(prev => {
                                                                                                let next = setView(prev, action.key, !aView);
                                                                                                if (!aView) {
                                                                                                    next[screen.key] = getLevel(next, screen.key) === 'full' ? 'full' : 'view';
                                                                                                }
                                                                                                return next;
                                                                                            });
                                                                                            setSaved(false);
                                                                                        }}
                                                                                        icon={<Eye size={12} />}
                                                                                        activeColor="#2563EB"
                                                                                    />
                                                                                    {showEditButtons && (
                                                                                        <IconToggle
                                                                                            active={aEdit}
                                                                                            onClick={() => {
                                                                                                setAccess(prev => {
                                                                                                    let next = setEdit(prev, action.key, !aEdit);
                                                                                                    if (!aEdit) {
                                                                                                        next[screen.key] = 'full';
                                                                                                        next[action.key] = 'full';
                                                                                                    }
                                                                                                    return next;
                                                                                                });
                                                                                                setSaved(false);
                                                                                            }}
                                                                                            icon={<Pencil size={11} />}
                                                                                            activeColor="#2563EB"
                                                                                        />
                                                                                    )}
                                                                                </div>
                                                                                <div className="w-6" />
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </>
                    )}
                </div>

                {/* ── Footer ── */}
                <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-between gap-3">
                    {error && <p className="text-[11px] text-red-500 font-medium">{error}</p>}
                    {!error && saved && (
                        <p className="text-[11px] text-emerald-600 font-semibold flex items-center gap-1">
                            <Check size={12} /> Saved!
                        </p>
                    )}
                    {!error && !saved && <span />}
                    <div className="flex items-center gap-2">
                        <button onClick={onClose} className="px-4 py-2 text-[12px] font-semibold text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                            Cancel
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={saving || loading || isSuperAdminTarget}
                            className="px-5 py-2 text-[12px] font-bold bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            title={isSuperAdminTarget ? 'Cannot modify SUPERADMIN — they always have full access' : 'Save configuration'}
                        >
                            {saving ? <Loader2 size={13} className="animate-spin" /> : null}
                            {saving ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default ScreenAccessModal;
